import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import { featureRequestCommentsApi } from '../../api/endpoints';
import { getApiErrorMessage, fetchFeatureRequestFileBlob } from '../../api/client';
import { notify } from '../../components/Notifier';
import { FileAttachInput } from '../../components/FileAttachInput';
import { SavedFileList } from '../../components/SavedFileList';
import { formatDateTime } from '../../utils/date';
import type { FeatureRequestComment } from '../../types';

interface RequestCommentThreadProps {
  requestId: string;
  /** Hiện ô soạn bình luận (cần permission FEATURE_REQUEST_COMMENT). */
  canComment: boolean;
}

/**
 * Khu "Trao đổi" trong dialog chi tiết yêu cầu chức năng: khách hàng và dev
 * bình luận qua lại, kèm ảnh/tài liệu đính kèm. Append-only — không sửa,
 * không xoá.
 */
export function RequestCommentThread({
  requestId,
  canComment,
}: RequestCommentThreadProps): JSX.Element {
  const [comments, setComments] = useState<FeatureRequestComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    featureRequestCommentsApi
      .list(requestId)
      .then((items) => {
        if (!cancelled) setComments(items);
      })
      .catch(() => {
        if (!cancelled) notify('Không tải được bình luận', 'warning');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      const created = await featureRequestCommentsApi.create(requestId, text, files);
      setComments((prev) => [...prev, created]);
      setBody('');
      setFiles([]);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không gửi được bình luận'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700}>
        Trao đổi
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Đang tải bình luận…
        </Typography>
      ) : comments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Chưa có bình luận nào.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {comments.map((comment) => (
            <Box
              key={comment.id}
              sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5, py: 0.5 }}
              data-testid="feature-request-comment"
            >
              <Typography variant="caption" color="text.secondary">
                {comment.author_name ?? 'Không rõ'} · {formatDateTime(comment.created_at)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {comment.body}
              </Typography>
              {comment.files.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <SavedFileList files={comment.files} fetchBlob={(f) => fetchFeatureRequestFileBlob(f.id)} />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {canComment && (
        <Stack spacing={1}>
          <TextField
            label="Viết bình luận"
            placeholder="Trao đổi thêm về yêu cầu này…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={sending}
          />
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <FileAttachInput value={files} onChange={setFiles} disabled={sending} iconButton />
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              disabled={sending || body.trim() === ''}
              onClick={() => void send()}
            >
              {sending ? 'Đang gửi…' : 'Gửi'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
